import { IApiClient } from '~/lib/ApiClient';
import ServerOptionsStore from '~/lib/store/ServerOptionsStore';
import { observable, computed, action, when, autorun, makeObservable, IReactionDisposer } from 'mobx';
import {
    Size,
    Image,
    Region,
    SizeType,
    Software,
    License,
    Server,
    ChangeSizeOptionsRequest,
    LicensedSoftware,
    ServiceLevel,
} from '~/lib/ApiModel';
import { LoadStatus } from '~/lib/store/LoadingTracker';
import { orderByLookup, orderBy, withItem, distinct } from '~/lib/helper/ArrayHelper';
import { removeChildErrors, removeError, ValidationErrors } from '~/lib/helper/ErrorHelper';
import {
    distributionsFromOperatingSystems,
    operatingSystemsForDistribution,
    getOperatingSystemSurcharge,
} from '~/lib/helper/OperatingSystemHelper';
import { applyLicenseGroupDefaults, getLicenseCost } from '~/lib/helper/LicenseHelper';
import {
    applySizeDefaultsToOptions,
    getSizeCost,
    normaliseStorage,
    SizeOptionsWithDefaultsApplied,
} from '../helper/SizeHelper';

export class ServerSizeConfigStore {
    serverOptions: ServerOptionsStore;

    // Store-lifetime reactions; a subclass pushes its own onto the same list and dispose() (called from the
    // owning component's componentWillUnmount) tears down the whole chain.
    protected readonly disposers: IReactionDisposer[] = [];

    constructor(
        public apiClient: IApiClient,
        server?: Server
    ) {
        this.serverOptions = new ServerOptionsStore(apiClient);
        this._server = server;
        makeObservable(this);

        this.disposers.push(
            when(
                () => this.serverOptions.sshKeys.status === LoadStatus.Complete,
                () =>
                    (this.selectedSshKeysIds = this.serverOptions.sshKeys.items
                        .filter((x) => x.default)
                        .map((k) => k.id))
            ),
            autorun(() => {
                if (this.selectedSize && this.selectedSizeSlug !== this.selectedSize.slug) {
                    this.selectSize(this.selectedSize.slug);
                }
            }),
            autorun(() => {
                // refresh our plans on operating system change (if we have already loaded at least once)
                if (
                    this.selectedOperatingSystem &&
                    this.selectedOperatingSystem.slug !== this.serverOptions.sizes.osSlug &&
                    this.serverOptions.sizes.status !== LoadStatus.Pending
                ) {
                    this.serverOptions.sizes.setOs(this.selectedOperatingSystem.slug ?? undefined);
                }
            })
        );
    }

    dispose() {
        this.disposers.forEach((d) => {
            d();
        });
    }

    initialize(): Promise<unknown>[] {
        return this.serverOptions.loadAll(
            this.server?.id,
            this.selectedOperatingSystem?.slug ?? this.server?.image.slug ?? undefined
        );
    }

    // Read-only server for lifetime of store.
    @observable
    private _server?: Server;

    @computed
    get server() {
        return this._server;
    }

    @observable
    hostname?: string = undefined;

    @observable
    protected selectedRegionId?: string = undefined;

    @observable
    protected selectedOperatingSystemId?: number = undefined;

    @observable
    protected selectedSizeSlug?: string = undefined;

    @observable
    protected selectedSizeTypeSlug?: string = undefined;

    @observable
    selectedUserData?: string = undefined;

    @observable
    selectedExtras: ChangeSizeOptionsRequest = {
        ipv4_addresses: 1,
    };

    @observable
    selectedSshKeysIds: number[] = [];

    @observable
    selectedLicenseOptions: License[] = [];

    @observable
    currentErrors: ValidationErrors = {};

    @observable
    agreeTerms = false;

    @observable
    serverLicenses: LicensedSoftware[] = [];

    @computed
    get validFacilities(): Region[] {
        const activeRegions = this.serverOptions.regions.items;

        // the current region is always valid
        if (!this.server?.region) return activeRegions;
        return withItem(activeRegions, this.server.region, (f) => f.slug);
    }

    @computed
    get selectedRegion(): Region | undefined {
        return (
            this.validFacilities.find((f) => f.slug === (this.selectedRegionId ?? this.server?.region.slug)) ??
            (this.validFacilities.length ? this.validFacilities[0] : undefined)
        );
    }

    @computed
    get validSizeTypes(): SizeType[] {
        const seen = new Set<string>();
        let types: SizeType[] = [];
        for (const size of this.displayableSizes) {
            if (!seen.has(size.size_type.slug)) {
                seen.add(size.size_type.slug);
                types.push(size.size_type);
            }
        }
        if (this.server?.size.size_type) {
            types = withItem(types, this.server.size.size_type, (pt) => pt.slug);
        }

        return orderByLookup(types, ['vps', 'dedicated'], (p) => p.slug);
    }

    @computed
    get selectedSizeType(): SizeType | undefined {
        return (
            this.validSizeTypes.find(
                (t) => t.slug === (this.selectedSizeTypeSlug ?? this.server?.size.size_type.slug)
            ) ?? (this.validSizeTypes.length ? this.validSizeTypes[0] : undefined)
        );
    }

    @computed
    get validOperatingSystems(): Image[] {
        let operatingSystems = this.serverOptions.operatingSystems.items;

        if (this.server?.image) {
            operatingSystems = withItem(operatingSystems, this.server.image, (i) => i.id);
        }

        const selectedRegion = this.selectedRegion;

        if (!selectedRegion) return [];

        return operatingSystems.filter((os) => os.regions.indexOf(selectedRegion.slug) >= 0);
    }

    @computed
    get selectedOperatingSystem(): Image | undefined {
        const match = this.validOperatingSystems.find(
            (os) => os.id === (this.selectedOperatingSystemId ?? this.server?.image.id)
        );

        if (match) return match;

        //find a default by sorting by prefered distro, then by perferred version
        if (this.validOperatingSystems.length === 0) return undefined;

        const preferredDistro = distributionsFromOperatingSystems(this.validOperatingSystems)[0];

        if (!preferredDistro) return undefined;

        return operatingSystemsForDistribution(preferredDistro, this.validOperatingSystems)[0];
    }

    //TODO Should this be plan+options supports OS?, the customer may have beefed up the Ram and Storage
    sizeSupportsOperatingSystem(size: Size, os: Image) {
        return os.min_disk_size <= size.disk && (!os.min_memory_megabytes || os.min_memory_megabytes <= size.memory);
    }

    // Sizes that should be rendered in the plan list — passes region + OS min-disk/min-memory
    // but not the availability filter. Used to drive both the size-type button list (via
    // `validSizeTypes`) and the plan list (where `available: false` entries render disabled).
    @computed
    get displayableSizes(): Size[] {
        const { selectedRegion, selectedOperatingSystem } = this;

        if (!selectedOperatingSystem || !selectedRegion) return [];

        let sizes = this.serverOptions.sizes.items.filter(
            (p) =>
                p.regions.indexOf(selectedRegion.slug) >= 0 &&
                this.sizeSupportsOperatingSystem(p, selectedOperatingSystem)
        );
        // Re-add the server's current plan only if the API response includes it. The API handles
        // grandfathered plan eligibility (placement group membership and minimum resource requirements).
        if (
            this.server?.size &&
            this.sizeSupportsOperatingSystem(this.server.size, selectedOperatingSystem) &&
            this.serverOptions.sizes.items.some((s) => s.slug === this.server!.size.slug)
        ) {
            sizes = withItem(sizes, this.server.size, (p) => p.slug);
        }
        return sizes.sort((a, b) =>
            a.price_monthly === b.price_monthly ? 0 : a.price_monthly < b.price_monthly ? -1 : 1
        );
    }

    // Displayable sizes filtered to the currently-selected size type, with the grandfathered
    // plan appended even when its size_type differs — SizeSelector renders it under a
    // "current plan" separator so change-size users can see where they are.
    @computed
    get displayableSizesForSelectedType(): Size[] {
        const { selectedSizeType, server } = this;
        if (!selectedSizeType) return [];
        let sizes = this.displayableSizes.filter((p) => p.size_type.slug === selectedSizeType.slug);
        if (server?.size && this.displayableSizes.some((s) => s.slug === server.size.slug)) {
            sizes = withItem(sizes, server.size, (p) => p.slug);
        }
        return sizes;
    }

    @computed
    get validSizes(): Size[] {
        // Grandfathered plan stays selectable even when !available so change-size's default
        // selection (server.size) isn't invalidated when the plan is flagged unavailable.
        const serverSize = this.server?.size;
        return this.displayableSizesForSelectedType.filter((p) => p.available || p.slug === serverSize?.slug);
    }

    @computed
    get validSoftware(): Software[] {
        const selectedOSSlug = this.selectedOperatingSystem?.slug;
        if (!selectedOSSlug) return [];

        const software = this.serverOptions.software.items;
        const validEnabledSoftware = software.filter(
            (x) => x.supported_operating_systems.indexOf(selectedOSSlug) !== -1
        );

        const validExistingSoftware = this.serverLicenses
            .map((s) => s.software)
            .filter((x) => x.supported_operating_systems.indexOf(selectedOSSlug) !== -1);

        return distinct(validEnabledSoftware.concat(validExistingSoftware), (s) => s.id);
    }

    @computed
    get selectedSize(): Size | undefined {
        return (
            this.validSizes.find((p) => p.slug === (this.selectedSizeSlug ?? this.server?.size.slug)) ??
            this.validSizes.find(
                (p) => this.selectedRegion?.slug && (p.regions_out_of_stock ?? []).indexOf(this.selectedRegion.slug) < 0
            )
        );
    }

    @computed
    get selectedSizeOptions(): SizeOptionsWithDefaultsApplied | undefined {
        if (!this.selectedSize) return undefined;

        // Only the selection is normalised: pricing the server's current configuration must use its actual disk.
        const options = applySizeDefaultsToOptions(this.selectedSize, this.selectedExtras);
        const currentDisk =
            this.server && this.selectedSize.slug === this.server.size.slug ? this.server.disk : undefined;
        return { ...options, disk: normaliseStorage(this.selectedSize, options.disk, currentDisk) };
    }

    /* a consistently ordered (to help with validation) list of licenses to install where each group license defaults to the cheapest option */
    @computed
    get selectedLicenses(): License[] {
        const lics = this.selectedLicenseOptions.filter(
            (l) => !!this.validSoftware.find((s) => s.id === l.software_id)
        ); //filter invalid software
        const result = applyLicenseGroupDefaults(lics, this.validSoftware);
        return orderBy(result, (l) => l.software_id);
    }

    @computed
    get selectedServiceLevel(): ServiceLevel | undefined {
        return this.serverOptions.serviceLevels.items.find(
            (sl) => sl.id === this.selectedSizeOptions?.service_level_id
        );
    }

    @computed
    get selectedOptionsCost(): number {
        if (!this.selectedSize || !this.selectedSizeOptions || !this.selectedOperatingSystem) return 0;

        return (
            getSizeCost(this.selectedSize, this.selectedSizeOptions, this.selectedServiceLevel) +
            getOperatingSystemSurcharge(
                this.selectedOperatingSystem,
                this.selectedSize,
                this.selectedSizeOptions,
                this.selectedServiceLevel
            ) +
            getLicenseCost(this.selectedLicenses, this.validSoftware)
        );
    }

    @action
    selectRegion(regionId: string) {
        this.selectedRegionId = regionId;
        this.currentErrors = removeError(this.currentErrors, 'region');
    }

    @action
    selectOperatingSystem(operatingSystemId: number | undefined) {
        this.selectedOperatingSystemId = operatingSystemId;
        this.currentErrors = removeError(this.currentErrors, 'image');
    }

    @action
    selectSize(sizeSlug?: string) {
        if (this.selectedSizeSlug !== sizeSlug) {
            if (this.server && sizeSlug === this.server.size.slug) {
                this.selectedExtras.memory = this.server.memory;
                this.selectedExtras.disk = this.server.disk;
                this.selectedExtras.transfer = this.server.selected_size_options?.transfer ?? this.server.size.transfer;
            } else {
                this.selectedExtras.memory = undefined;
                this.selectedExtras.disk = undefined;
                this.selectedExtras.transfer = undefined;
            }
        }

        this.selectedSizeSlug = sizeSlug;
        this.currentErrors = removeError(this.currentErrors, 'size');
    }

    @action
    selectSizeType(sizeTypeId: string) {
        this.selectedSizeTypeSlug = sizeTypeId;
        // Clear size and all options errors - changing size type changes the available plans
        // and options, so any previous validation errors for these fields are stale.
        this.currentErrors = removeChildErrors(removeError(this.currentErrors, 'size'), 'options');
    }

    @action
    setHostname(hostname: string) {
        this.hostname = hostname;
        this.currentErrors = removeError(this.currentErrors, 'name');
    }

    @action
    updateSelectedExtras(opts: Partial<ChangeSizeOptionsRequest>) {
        for (const optName of Object.keys(opts)) {
            this.currentErrors = removeError(this.currentErrors, 'options.' + optName);
            // clearing errors for these fields should clear them for their dependent fields
            if (optName === 'networks_to_remove') {
                this.currentErrors = removeError(this.currentErrors, 'options.networks');
            }
            if (optName === 'networks') {
                this.currentErrors = removeError(this.currentErrors, 'options.networks_to_remove');
            }
        }

        this.selectedExtras = { ...this.selectedExtras, ...opts };
    }

    @action
    toggleSelectedSshKey(id: number) {
        if (this.selectedSshKeysIds.find((i) => i === id)) {
            this.selectedSshKeysIds = this.selectedSshKeysIds.filter((i) => i !== id);
        } else {
            this.selectedSshKeysIds.push(id);
        }
        this.currentErrors = removeChildErrors(this.currentErrors, 'ssh_keys');
    }

    @action
    selectLicenses(licenses: License[]) {
        this.currentErrors = removeError(removeChildErrors(this.currentErrors, 'licenses'), 'licenses');
        this.selectedLicenseOptions = licenses;
    }

    @action
    setUserData(userData?: string) {
        this.currentErrors = removeError(this.currentErrors, 'user_data');
        this.selectedUserData = userData;
    }

    @action
    setAgreeTerms(agree: boolean) {
        this.currentErrors = removeError(this.currentErrors, 'agreeTerms');
        this.agreeTerms = agree;
    }
}
